import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import apiFetch, { createApiAsyncThunk } from '@/services/apiService'
import { setTimeoutInDev } from '@/utils/environment'
import { addFlashMessage } from '../flash/flashMessagesSlice'
import { hasFiles, sanitizeFormData, transformToFormData } from '@/utils/formData'

const URL = 'biens'

export const bienFilters = {
  maison: {label: "Maisons", value: 'maison', key: 'type'},
  appartement: {label: "Appartements", value: 'appartement', key: 'type'},
  immeuble: { label: "Immeubles", value: 'immeuble', key: 'type'},
  autre: { label: "Autres", value: "autre", key: 'type'},
  vente: {label: "Ventes", value: 'vente', key: 'mandat'},
  location: {label: "Locations", value: 'location', key: 'mandat'},
}

export const bienFiltersIds = [
  "vente",
  "location",
  "maison",
  "appartement",
  "immeuble",
  "autre"
]

// Retourne dans un ordre prédéterminé les filtres disponibles
// avec la valeur "checked" à true s'ils sont séléctionnés
// dans le state
// exemple :
// state est ainsi : currentFilters = { type: "maison" }
// retournera {
//    { label: "Maisons", value: "maison", key: "type", checked: true },
//    { ... , checked: false },
//    ...
// } 
export const ordonnedBienFilters = (currentFilters) => bienFiltersIds.map(
  key => ({
    ...bienFilters[key],
    checked: currentFilters[bienFilters[key].key] === bienFilters[key].value
  })
)

// États initiaux
const initialState = {
  items: [],
  current: null,
  inEdition: null,
  loadingStatus: {
    fetchList: false,
    fetchItem: false,
    create: false,
    update: false,
    restore: false,
    archive: false,
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {}
  },
  filters: {
    page: 1,
    limit: 100
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  },
  currentProprietaireIndex: null
}

const sanitizeBienFormData = (data) => {
  const formData = sanitizeFormData(data)
  if(["appartement", "autre"].includes(formData.type_bien)) {
    if(formData.immeuble && formData.immeuble.id && formData.immeuble.type_bien) {
      formData.immeuble_id = formData.immeuble.id
    }
    if(formData.immeuble_id && !formData.immeuble) {
      formData.immeuble_id = null
    }
  }
  delete formData.immeuble
  delete formData.label
  delete formData.numero_mandats

  // delete formData.notaire_vendeur
  // delete formData.notaire_acquereur
  formData.proprietaires = formData.proprietaires?.map(prop => ({contact_id: prop.id }))
  formData.acquereurs = formData.acquereurs?.map(prop => ({contact_id: prop.id}))

  // Utiliser la détection automatique des champs de fichier
  // Le tableau fileFields est toujours utilisé pour la suppression des champs undefined ou vides
  let result
  if(hasFiles(formData)) {
    // Nous n'avons plus besoin de passer fileFields car la détection est automatique
    result = transformToFormData(formData, "bien")
  }
  return result ? result : { bien: formData}
}

export const formatBienAddress = (
  bien,
  options = { complete: false }
) => {
  if (!bien) return ''

  // Création d'un label avec les informations pertinentes
  const adresse = [
    ...(options.complete ? [
    bien.adresse_ligne1,
    bien.adresse_ligne2,
    bien.adresse_ligne3,
    ] : [ bien.adresse_ligne1 ]),
    bien.ville,
    bien.code_postal,
    ...(options.complete ? [bien.pays] : [])
  ]

  return adresse.filter(Boolean).join(', ')
}

export const formatBienLabel = (
  bien, 
  options = {}
) => {
  if (!bien) return ''

  const { withType=true, withAddress=true} = options
  
  const desc = bien.type_bien === 'appartement' ? 
    ` n° ${bien.numero_appartement} ` : 
    ``
  const adresse = withAddress ? formatBienAddress(bien) : ''
  
  const typeBien = withType && bien.type_bien ? `${bien.type_bien.toUpperCase()} - ` : ''
  
  return `${typeBien}${desc}${adresse}`
}

// Thunks
export const fetchBiens = createAsyncThunk(
  'biens/fetchBiens',
  async (params = {}, { rejectWithValue }) => {
    try {
      await setTimeoutInDev()
      let url = URL
      if (params.contact_id) {
        url = `contacts/${params.contact_id}/biens`
        delete params.contact_id
      }
      if (params.immeuble_id) {
        url = `biens/${params.immeuble_id}/unites`
        delete params.immeuble_id
      }
      const response = await apiFetch()
        .url(url)
        .query(params)
        .get()
      return response
    } catch (error) {
      console.error('Error fetching biens:', error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des biens')
    }
  }
)

export const searchBiens = createApiAsyncThunk(
  'biens/searchBiens',
  async (params = {}, { api, rejectWithValue }) => {
    try {
      let url = URL
      if(params.term) {
        url = `${url}/search`
      }
      const response = await api
        .url(url)
        .query(params)
        .get()
      return response
    } catch (error) {
      console.error(error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des documents')
    }
  }
)

export const fetchBien = createAsyncThunk(
  'biens/fetchBien',
  async (id, { dispatch, getState, rejectWithValue }) => {
    await setTimeoutInDev()
    try {
      const response = await apiFetch()
        .url(`${URL}/${id}`)
        .get()
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération du bien')
    }
  }
)

export const createBien = createApiAsyncThunk(
  'biens/createBien',
  async (bienData, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(URL)
        .post(sanitizeBienFormData(bienData))
      dispatch(addFlashMessage({type: 'success', text: "Bien créé avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const updateBien = createApiAsyncThunk(
  'biens/updateBien',
  async ({ id, bienData }, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${id}`)
        .put(sanitizeBienFormData(bienData))
      dispatch(setCurrentBien(response))
      dispatch(addFlashMessage({type: 'success', text: "Bien mis à jour avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const archiveBien = createApiAsyncThunk(
  'biens/archiveBien',
  async (id, { api, dispatch, getState, rejectWithValue }) => {
    try {
      await setTimeoutInDev()
      const response = await api
        .url(`${URL}/${id}/archive`)
        .put()
      const current = getState().biens.current
      if(current && response.id === current.id) {
        dispatch(setCurrentBien(response))
      }
      dispatch(addFlashMessage({type: 'success', text: 'Bien archivé'}))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const restoreBien = createApiAsyncThunk(
  'biens/restoreBien',
  async (id, { api, dispatch, getState, rejectWithValue }) => {
    try {
      await setTimeoutInDev()
      const response = await api
        .url(`${URL}/${id}/restore`)
        .put()
      const current = getState().biens.current
      if(current && response.id === current.id) {
        dispatch(setCurrentBien(response))
      }
      dispatch(addFlashMessage({type: 'success', text: 'Bien archivé'}))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteBien = createApiAsyncThunk(
  'biens/deleteBien',
  async (id, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${id}`)
        .delete()

      dispatch(addFlashMessage({type: 'success', text: "Bien a été supprimé avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const updateEntretienRegulierBien = createApiAsyncThunk(
  'biens/updateEntretienRegulier',
  async ({id, nom, data}, {api, dispatch, fulfillWithValue, rejectWithValue}) => {
    try {
      const response = await api
        .url(`${URL}/${id}/entretien_regulier`)
        .put({entretien_config: data})
      dispatch(recordEntretienRegulier(data))
      dispatch(addFlashMessage({
        type: 'success', 
        text: `L'entretien "${nom}" est modifié`
      }))
      return fulfillWithValue()
    } catch (error) {
      return rejectWithValue(error.data || {message: error.message, errors: {}})
    }
  }
)
// Valeurs par défaut pour un bien vide
export const emptyBien = {
  adresse_ligne1: '',
  adresse_ligne2: '',
  adresse_ligne3: '',
  code_postal: '',
  ville: '',
  pays: 'France',
  type_bien: '',
  disponibilite: true,
  date_construction: '',
  nombre_etages: '',
  cadastre: '',
  ref_batiment: '',
  ref_escalier: '',
  ref_etage: '',
  numero_appartement: '',
  numero_sonnette: '',
  proprietaires: [],
  immeuble: null
}

const sanitizesPayload = (action) => Object.fromEntries(
  Object.entries(action.payload).map(([key, value]) =>
    [key, value === null ? (emptyBien[key] || "") : value]
  )
)

// Slice
const biensSlice = createSlice({
  name: 'biens',
  initialState,
  reducers: {
    setEmptyCurrentBien: (state, action) => {
      const bienData = {
        ...emptyBien,
        ...action.payload
      }
      state.current = {
        ...bienData,
        label: formatBienLabel(bienData)
      }
      state.requestStatus = initialState.requestStatus
    },
    setCurrentBien: (state, action) => {
      const sanitizedData = {
        ...emptyBien,
        ...sanitizesPayload(action)
      }
      state.current = {
        ...sanitizedData,
        label: formatBienLabel(sanitizedData)
      }
      state.requestStatus = initialState.requestStatus
    },
    updateCurrentBien: (state, action) => {
      state.current = {
        ...state.current,
        ...sanitizesPayload(action)
      }
    },
    resetCurrentBien: (state) => {
      state.current = initialState.current
      state.requestStatus = initialState.requestStatus
    },
    setBienInEdition: (state, action) => {
      state.inEdition = {
        ...emptyBien,
        ...action.payload
      }
      state.requestStatus = initialState.requestStatus
    },
    resetBienInEdition: (state) => {
      state.inEdition = initialState.inEdition
      state.requestStatus = initialState.requestStatus
    },
    recordEntretienRegulier: (state, action) => {
      const entretiens = state.current?.entretiens_reguliers
      if(entretiens) {
        const updatedEntretiens = entretiens.map( item => 
          item.entretien_type_id === action.payload.entretien_type_id ?
            { ...item, ...action.payload }:
            item
        )
        state.current.entretiens_reguliers = updatedEntretiens
      }
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    },
    // Actions pour la gestion de la création de propriétaire
    startNewOwnerCreation: (state, action) => {
      state.isCreatingNewOwner = true
      state.currentProprietaireIndex = action.payload?.index ?? null
    },
    finishNewOwnerCreation: (state, action) => {
      const newProprietaire = {
        contact_id: action.payload.id,
        nom_complet: action.payload.nom_complet || `${action.payload.prenom} ${action.payload.nom}`,
        email: action.payload.email,
        telephone: action.payload.telephone,
        adresse: action.payload.adresse,
      }

      // Si nous avons un index spécifique, nous mettons à jour ce propriétaire
      if (state.currentProprietaireIndex !== null && state.current?.proprietaires) {
        state.current.proprietaires[state.currentProprietaireIndex] = newProprietaire
      }
      // Sinon, nous ajoutons un nouveau propriétaire
      else if (state.current?.proprietaires) {
        state.current.proprietaires.push(newProprietaire)
      } else {
        state.current = {
          ...state.current,
          proprietaires: [newProprietaire]
        }
      }

      state.isCreatingNewOwner = false
      state.currentProprietaireIndex = null
    },
    cancelNewOwnerCreation: (state) => {
      state.isCreatingNewOwner = false
      state.currentProprietaireIndex = null
    }
  },
  extraReducers: (builder) => {
    // fetchBiens
    builder.addCase(fetchBiens.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchBiens.fulfilled, (state, action) => {
      state.items = action.payload.biens
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchBiens.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })
    builder.addCase(searchBiens.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(searchBiens.fulfilled, (state, action) => {
      state.items = action.payload.data
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(searchBiens.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })

    // fetchBien
    builder.addCase(fetchBien.pending, (state) => {
      state.loadingStatus.fetchItem = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchBien.fulfilled, (state, action) => {
      state.current = {
        ...action.payload,
        label: formatBienLabel(action.payload)
      }
      state.loadingStatus.fetchItem = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchBien.rejected, (state, action) => {
      state.loadingStatus.fetchItem = false
      state.requestStatus.error = action.payload
    })

    // createBien
    builder.addCase(createBien.pending, (state) => {
      state.loadingStatus.create = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(createBien.fulfilled, (state, action) => {
      state.items.unshift(action.payload)
      state.loadingStatus.create = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(createBien.rejected, (state, action) => {
      state.loadingStatus.create = false
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.error = action.payload
      state.requestStatus.success = false
})

    // updateBien
    builder.addCase(updateBien.pending, (state) => {
      state.loadingStatus.update = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(updateBien.fulfilled, (state, action) => {
      const updatedBien = action.payload
      const index = state.items.findIndex(bien => bien.id === updatedBien.id)
      if (index !== -1) {
        state.items[index] = updatedBien
      }
      state.loadingStatus.update = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(updateBien.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.success = false
    })

    // Update un entretien régulier d'un bien
    builder.addCase(updateEntretienRegulierBien.pending, (state) => {
      state.loadingStatus.update = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(updateEntretienRegulierBien.fulfilled, (state) => {
      state.loadingStatus.update = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(updateEntretienRegulierBien.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.success = false
    })

    // Archive Bien  
    builder.addCase(archiveBien.pending, (state) => {
      state.loadingStatus.archive = true
      state.requestStatus.error = null
    })
    builder.addCase(archiveBien.fulfilled, (state, action) => {
      state.loadingStatus.archive = false
      const index = state.items.findIndex(bien => bien.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      state.requestStatus.success = true
    })
    builder.addCase(archiveBien.rejected, (state, action) => {
      state.loadingStatus.archive = false
      state.requestStatus.error = action.payload
    })
    
    // Restore Bien
    builder.addCase(restoreBien.pending, (state) => {
      state.loadingStatus.restore = true
      state.requestStatus.error = null
    })
    builder.addCase(restoreBien.fulfilled, (state, action) => {
      state.loadingStatus.restore = false
      const index = state.items.findIndex(bien => bien.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      state.requestStatus.success = true
    })
    builder.addCase(restoreBien.rejected, (state, action) => {
      state.loadingStatus.restore = false
      state.requestStatus.error = action.payload
    })

    // Delete Bien
    builder.addCase(deleteBien.pending, (state) => {
      state.loadingStatus.delete = true
      state.requestStatus.error = null
    })
    builder.addCase(deleteBien.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      const index = state.items.findIndex( item => item.id === action.payload.id)
      if (index !== -1) {
        state.items.splice(index, 1)
      }
      state.requestStatus.success = true
    })
    builder.addCase(deleteBien.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })
  }
})

export const getHasMandatOfTypeFunc = (bien) => 
  (type) => type && bien && bien.mandats && bien?.mandats?.map( m => m.type_mandat).includes(type)

export const getBienSuggestionLabel = (bien = {}) => {
  let label = ''
  if(bien?.codage_interne) {
    label = bien.codage_interne
  } 
  else {
    const originalLabel = `${bien?.ville} ${bien?.adresse_ligne1}${bien?.type_bien === 'appartement' ? ` - ${bien?.numero_appartement}` : ''}`
    label = `${originalLabel.trim().length > 0 ? originalLabel : formatBienAddress(bien)}`
  }
  label += `${bien?.numero_mandats?.length > 0 ? ` (${bien.numero_mandats.join(' / ')})` : ''}`
  return label
}

export const { 
  setEmptyCurrentBien,
  setCurrentBien,
  updateCurrentBien,
  resetCurrentBien,
  setBienInEdition,
  resetBienInEdition,
  recordEntretienRegulier,
  setFilters,
  resetRequestStatus,
  // Actions pour la création de propriétaire
  startNewOwnerCreation,
  finishNewOwnerCreation,
  cancelNewOwnerCreation
} = biensSlice.actions

export default biensSlice.reducer